import React from 'react';
import { useNavigate } from 'react-router-dom';
import { computeRiskMetrics } from '../services/risk/mockData.js';
import { computeMetrics, PORTS_DATA } from '../services/bunker/mockData.js';

const riskM   = computeRiskMetrics();
const bunkerM = computeMetrics(PORTS_DATA);

// Inject responsive grid CSS
if (typeof document !== 'undefined' && !document.getElementById('fv-dash-css')) {
  const s = document.createElement('style');
  s.id = 'fv-dash-css';
  s.textContent = `
    .fv-module-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 16px;
    }
    @media (min-width: 1100px) {
      .fv-module-grid { grid-template-columns: repeat(3, 1fr); }
    }
    @media (max-width: 640px) {
      .fv-module-grid { grid-template-columns: 1fr; }
    }
  `;
  document.head.appendChild(s);
}

const MODULES = [
  {
    path: '/bunker', title: 'Bunker Prices', icon: '⛽',
    accentColor: '#4F46E5',
    status: 'live',
    tagline: 'Where should I bunker today?',
    kpis: [
      { label: 'Global Avg VLSFO', value: `$${bunkerM.globalAvg}/mt` },
      { label: 'Cheapest Port',    value: `${bunkerM.cheapest.name}` },
      { label: 'Cheapest Price',   value: `$${bunkerM.cheapest.price}/mt` },
      { label: 'Biggest 24h Move', value: `${bunkerM.biggestMove.change > 0 ? '+' : ''}$${bunkerM.biggestMove.change}` },
    ],
  },
  {
    path: '/risk', title: 'War Risk & Insurance', icon: '🛡',
    accentColor: '#DC2626',
    status: 'live',
    tagline: 'What are the hidden insurance costs?',
    kpis: [
      { label: 'Active Threat Zones',  value: `${riskM.activeZones} of ${riskM.totalZones}` },
      { label: 'Incidents This Month', value: `${riskM.attacksThisMonth}` },
      { label: 'Hormuz Premium',       value: `${riskM.hormuzPremiumTypical}% hull` },
      { label: 'Critical Incidents',   value: `${riskM.criticalIncidents}` },
    ],
  },
  {
    path: '/routes', title: 'Route Cost Comparator', icon: '🗺',
    accentColor: '#059669',
    status: 'soon',
    tagline: 'Suez vs Cape vs Hormuz — total cost',
    kpis: [],
  },
  {
    path: '/compliance', title: 'EU ETS & FuelEU', icon: '🌿',
    accentColor: '#D97706',
    status: 'soon',
    tagline: 'Carbon compliance cost tracker',
    kpis: [],
  },
  {
    path: '/brief', title: 'AI Daily Brief', icon: '✦',
    accentColor: '#00A896',
    status: 'soon',
    tagline: 'What do I need to know by 8AM?',
    kpis: [],
  },
];

export default function Dashboard() {
  const navigate = useNavigate();

  return (
    <div style={{ minHeight: 'calc(100vh - 58px)', display: 'flex', flexDirection: 'column', background: 'var(--bg)' }}>
      {/* Page header */}
      <div style={{
        background: 'var(--card)', padding: '22px 28px 20px',
        borderBottom: '1px solid var(--border)',
      }}>
        <div style={{
          fontFamily: "'Figtree', sans-serif", fontSize: 12,
          color: '#00A896', letterSpacing: '1px', marginBottom: 6,
          fontWeight: 500, textTransform: 'uppercase',
        }}>Overview</div>
        <div style={{
          fontFamily: "'Fraunces', serif", fontSize: 24,
          fontWeight: 700, color: 'var(--text)', letterSpacing: '-0.5px',
        }}>FathomVoyage Dashboard</div>
        <div style={{ fontSize: 13, color: 'var(--text-3)', marginTop: 3 }}>
          Voyage Cost Intelligence — April 2026
        </div>
      </div>

      {/* Alert bar */}
      <div style={{
        background: 'rgba(220,38,38,0.1)', borderBottom: '1px solid rgba(220,38,38,0.2)',
        padding: '10px 28px', fontSize: 13, fontWeight: 500,
        color: '#ef4444', display: 'flex', gap: 10, alignItems: 'flex-start',
        lineHeight: 1.4,
      }}>
        <span style={{ flexShrink: 0 }}>🔴</span>
        <span>
          <strong>Active alert:</strong> Strait of Hormuz war risk premium elevated to 0.50% hull value
          · 8 incidents in 30 days · IRGC boarding reported 04 Apr 2026
        </span>
      </div>

      {/* Module grid */}
      <div style={{ padding: '20px 24px', flex: 1 }}>
        <div className="fv-module-grid">
          {MODULES.map(m => (
            <ModuleCard key={m.path} module={m} onClick={() => navigate(m.path)} />
          ))}
        </div>
      </div>

      {/* Footer */}
      <div style={{
        borderTop: '1px solid var(--border)', padding: '12px 28px',
        display: 'flex', justifyContent: 'space-between',
        flexWrap: 'wrap', gap: 8,
      }}>
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: 'var(--text-3)' }}>
          FATHOMVOYAGE.COM · DEMO MODE · ALL DATA INDICATIVE
        </span>
        <span style={{ fontSize: 11, color: 'var(--text-3)' }}>© 2026 FathomVoyage</span>
      </div>
    </div>
  );
}

function ModuleCard({ module: m, onClick }) {
  const [hovered, setHovered] = React.useState(false);
  const isLive = m.status === 'live';

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered && isLive ? 'var(--card-hover)' : 'var(--card)',
        border: `1px solid ${hovered && isLive ? 'var(--border-mid)' : 'var(--border)'}`,
        borderRadius: 14,
        padding: '20px 20px 18px',
        cursor: isLive ? 'pointer' : 'default',
        transition: 'box-shadow 0.15s, border-color 0.15s, transform 0.15s, background 0.15s',
        boxShadow: hovered && isLive ? 'var(--shadow-md)' : 'var(--shadow-sm)',
        transform: hovered && isLive ? 'translateY(-1px)' : 'none',
        opacity: isLive ? 1 : 0.7,
        display: 'flex', flexDirection: 'column', gap: 14,
        position: 'relative', overflow: 'hidden',
      }}>

      {/* Accent bar */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0,
        height: 3, background: m.accentColor,
        opacity: isLive ? 1 : 0.3,
      }} />

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <div style={{
            fontSize: 24, lineHeight: 1, marginTop: 1,
            width: 40, height: 40,
            background: m.accentColor + '20',
            borderRadius: 10,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            {m.icon}
          </div>
          <div>
            <div style={{
              fontFamily: "'Fraunces', serif", fontSize: 15,
              fontWeight: 600, color: 'var(--text)', lineHeight: 1.2,
            }}>
              {m.title}
            </div>
            <div style={{ fontFamily: "'Figtree', sans-serif", fontSize: 12, color: 'var(--text-3)', marginTop: 4, lineHeight: 1.3 }}>
              {m.tagline}
            </div>
          </div>
        </div>
        <span style={{
          padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
          fontFamily: "'JetBrains Mono', monospace", flexShrink: 0,
          background: isLive ? 'rgba(16,185,129,0.12)' : 'var(--bg-alt)',
          color:       isLive ? '#10B981' : 'var(--text-3)',
        }}>
          {isLive ? '● LIVE' : 'SOON'}
        </span>
      </div>

      {/* KPI grid */}
      {isLive && m.kpis.length > 0 ? (
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8,
        }}>
          {m.kpis.map(k => (
            <div key={k.label} style={{
              background: 'var(--bg-alt)', borderRadius: 8, padding: '10px 12px',
              border: '1px solid var(--border)',
            }}>
              <div style={{
                fontSize: 12, color: 'var(--text-3)', fontWeight: 600,
                textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4,
              }}>
                {k.label}
              </div>
              <div style={{
                fontFamily: "'JetBrains Mono', monospace", fontSize: 13,
                fontWeight: 500, color: 'var(--text)',
              }}>
                {k.value}
              </div>
            </div>
          ))}
        </div>
      ) : !isLive ? (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '12px 14px', borderRadius: 8,
          background: 'var(--bg-alt)', border: '1px solid var(--border)',
          color: 'var(--text-3)', fontSize: 13,
        }}>
          🔒 <span>Coming soon — building now</span>
        </div>
      ) : null}

      {/* CTA arrow for live modules */}
      {isLive && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
          fontSize: 12, fontWeight: 500,
          color: hovered ? m.accentColor : 'var(--text-3)',
          transition: 'color 0.15s',
          gap: 4,
        }}>
          Open module
          <span style={{
            transition: 'transform 0.15s',
            transform: hovered ? 'translateX(3px)' : 'none',
            display: 'inline-block',
          }}>→</span>
        </div>
      )}
    </div>
  );
}
